const { window } = require('browser-monads-ts');

export const childrenToArray = function __childrenToArray<T>(children?: T[] | T):T[] {
  if(typeof children !== 'undefined') {
    return Array.isArray(children) ? children : [children];
  }

  return []
}

export const splitArray = function __splitArray(arr: any[], desiredLength: number) {
  const remainder = arr.length % desiredLength;
  
  // If there's no remainder, return the array as is.
  if (remainder === 0) {
    return arr;
  }
  
  // Get the full part (elements up to the remainder)
  const fullPart = arr.slice(0, arr.length - remainder);
  
  // Get the remainder part (the leftover elements)
  const remainderPart = arr.slice(arr.length - remainder);
  
  // Return the full part with the remainder wrapped in its own array.
  return [...fullPart, remainderPart];
}

export const generateUUID = function __generateUUID() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => {
        const num = parseInt(c);
        return (num ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> num / 4).toString(16);
    });
}

export const validateBool = function __validateBool (val: string, set: { [key: string]: boolean; }, defaultVal: string) {
  if(typeof set[val] === 'undefined') {
    return set[defaultVal];
  }

  return set[val];
}

export const isUrlExternal = function __isUrlExternal(url = ''){
  return (url.indexOf('http://') === 0 || url.indexOf('https://') === 0);
}

export const hasParameter = function __hasParameter(parameterName: string) {
  const params = new URLSearchParams(window.location.search);
  return params.has(parameterName);
}

export const findGetParameter = function __findGetParameter(parameterName: string, sanitize = true): string {
  const urlParams = new URLSearchParams(window.location.search);
  const result = urlParams.get(parameterName) || '';
  return sanitize ? __customSanitizeString(result) : result;
}

const __customSanitizeString = function __customSanitizeString(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, '+').split(' ').join('+');
}

export function stringToBoolean(value: string | boolean): boolean {
  return ['true', 'True', true].includes(value);
}

export const unslash = function __unslash(slug: string) {
  return slug.trim().replace(/^\/+|\/+$/g, '');
}

interface Related {
    originalId: string;
    [key: string]: any;
};

export const getFormattedDateTime = function __getFormattedDateTime(timeZone: string) {
  const now = new Date();
  
  // Get date and time formatted in the given time zone
  const optionsDate: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone };
  const optionsTime: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone };

  const dateString = new Intl.DateTimeFormat('en-CA', optionsDate).format(now).replace(/\//g, '-');
  const timeString = new Intl.DateTimeFormat('en-GB', optionsTime).format(now);

  return {
      'date': dateString,
      'time': timeString
  };
}

export const renderDate = function __renderDate(start: string, end: string, locale: Locale) {
    const optionsFull:  Intl.DateTimeFormatOptions = {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      timeZone: 'Europe/Brussels'
    };

    const optionsShort: Intl.DateTimeFormatOptions = {
      month: 'short', 
      day: 'numeric',
      timeZone: 'Europe/Brussels' 
    };

    const optionsYear: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      timeZone: 'Europe/Brussels'
    };

    const startDate = new Date(start);
    const endDate = new Date(end);

    const startYear = (startDate.getFullYear() !== endDate.getFullYear()) ? ' ' + startDate.toLocaleDateString(locale, optionsYear) : '';
    return (start === end) ? startDate.toLocaleDateString(locale, optionsFull) : startDate.toLocaleDateString(locale, optionsShort) + startYear  + ' - ' + endDate.toLocaleDateString(locale, optionsShort) + ' ' + endDate.toLocaleDateString(locale, optionsYear);
}

export interface dateType {
  date: string
}
export const sortDates = function __sortDates(a: dateType, b: dateType) {
  return (new Date(a.date) < new Date(b.date)) ? 1 : -1;
}

interface videoType {
  gatingPage: {
    url: string
  } | null;
  url: string;
}

export const videoLink = function __videoLink(video: videoType) {
  if(video.gatingPage !== null) {
    return video.gatingPage.url;
  }
  
  return video.url;
}

interface contextType {
  context: {
    originalId: string
    locale: Locale
  }
  path: string
}

interface routeType {
  [key: string]: Partial<Alternate>;
}

export const buildMiniRoutes = function __buildMiniRoutes(array: contextType[]) {
  return array.reduce((acc: routeType, val) => {
    if(!acc[val.context.originalId]) {
      acc[val.context.originalId] = {};
    }

    acc[val.context.originalId][val.context.locale] = val.path;
    
    return acc;
  }, {} as routeType);
}

interface StorageData<T = any> {
  data: T;
  timestamp?: number;
  expiry?: number;
}

export const safeLocalStorage = {
  get: <T = any>(key: string, expiryHours?: number): T | null => {
    if (typeof window === 'undefined') return null;
    
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      
      // Try to parse as cached data first
      try {
        const parsed: StorageData<T> = JSON.parse(item);
        
        // Check if it has cache properties and if expiry is set
        if (parsed.timestamp && parsed.expiry && expiryHours !== undefined) {
          const now = Date.now();
          const expiryTime = parsed.expiry * 60 * 60 * 1000;
          
          // Check if cache is expired
          if (now - parsed.timestamp > expiryTime) {
            localStorage.removeItem(key);
            return null;
          }
          
          return parsed.data;
        }
        
        // If it has the data property but no expiry check needed
        if ('data' in parsed && 'timestamp' in parsed) {
          return parsed.data;
        }
        
        // Otherwise return the parsed value as is (backward compatibility)
        return parsed as unknown as T;
      } catch {
        // If JSON parse fails, return as string (backward compatibility)
        return item as unknown as T;
      }
    } catch (error) {
      console.error(`Error reading from localStorage key "${key}":`, error);
      return null;
    }
  },
  
  set: <T = any>(key: string, value: T, expiryHours?: number): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      let dataToStore: string;
      
      if (expiryHours !== undefined) {
        // Store with timestamp and expiry
        const storageData: StorageData<T> = {
          data: value,
          timestamp: Date.now(),
          expiry: expiryHours
        };
        dataToStore = JSON.stringify(storageData);
      } else if (typeof value === 'string') {
        // Store string directly for backward compatibility
        dataToStore = value;
      } else {
        // Store other types as JSON
        dataToStore = JSON.stringify(value);
      }
      
      localStorage.setItem(key, dataToStore);
      return true;
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
      return false;
    }
  },
  
  remove: (key: string): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }
};